import * as Sentry from '@sentry/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import {
  ChatSocketMessageType,
  IChatMessage,
  ISocketConnectedResponse,
  SocketAgentActivity,
  SocketAiResponseStart,
  SocketErrorResponse,
  SocketGenerationCancelled,
  SocketMessageResponseChunk,
  SocketMessageResponseComplete,
} from '../../../schemas/agent-conversation.schema';
import { queryClient } from '../../../services/MaggieQueryClient';
import { cancelAgentQuery, createAgentConversation } from '../../../services/queries/AgentQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { MessageData } from '../components/Message';
import { UiGeneratedIdPrefix } from '../utils/feedbackUtils';
import { AgentActivityAtom, clearTurnActivity, reduceAgentActivity } from './agentActivityAtom';
import { ChatUiContextAtom } from './chatUiContextAtom';
import {
  AgentWsIdentityAtom,
  ChatConversationIdAtom,
  ChatConversationsQueryKey,
  ChatErrorStateAtom,
  ChatMessagesAtom,
  IsCancellingChatGenerationAtom,
  IsReceivingMessagesAtom,
} from './chatActionHooks';
import { useWsChat } from './useWsChat';

interface UseChatReturn {
  messages: MessageData[];
  isTyping: boolean;
  isCancelling: boolean;
  sendMessage: (content: string) => void;
  onCancelGeneration: () => void;
}

const generateId = (): string => {
  return `${UiGeneratedIdPrefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
};

export const useChat = (): UseChatReturn => {
  const [messages, setMessages] = useAtom(ChatMessagesAtom);
  const [isTyping, setIsTyping] = useAtom(IsReceivingMessagesAtom);
  // Placeholder ids of replies the user cancelled. Frames for them that were
  // already in flight (the REST cancel and the socket aren't ordered) would
  // otherwise recreate a bubble nothing will ever finalize.
  const cancelledMessageIdsRef = useRef(new Set<number>());
  const [isCancelling, setIsCancelling] = useAtom(IsCancellingChatGenerationAtom);
  const [conversationId, setConversationId] = useAtom(ChatConversationIdAtom);
  const setChatErrorState = useSetAtom(ChatErrorStateAtom);
  const setAgentActivity = useSetAtom(AgentActivityAtom);
  const setAgentWsIdentity = useSetAtom(AgentWsIdentityAtom);

  const getConversationId = useCallback(async () => {
    // when a new conversation is created, ChatConversationIdAtom is reset to undefined,
    // but in order to allow cancelling the generation, we need that id as soon as the first message is sent
    if (conversationId) {
      return conversationId;
    }
    try {
      // reset error state when creating a new conversation
      setChatErrorState(null);
      const newConversation = await createAgentConversation({});
      setConversationId(newConversation.id);
      return newConversation.id;
    } catch (error) {
      setChatErrorState(getErrorMessage(error, 'Something went wrong, please try again'));
      return null;
    }
  }, [conversationId, setConversationId, setChatErrorState]);

  // Frames tagged with another conversation's id are stale/foreign — drop
  // them. Untagged frames (null/undefined id, e.g. a brand-new conversation's
  // first turn or an older agent deployment) are assumed to be for the active
  // one.
  const isForeignFrame = useCallback(
    (frameConversationId: number | null | undefined) =>
      frameConversationId != null && conversationId != null && frameConversationId !== conversationId,
    [conversationId]
  );

  // A generation began: create the bubble the stream will render into, keyed
  // by the server's placeholder row id.
  const onAiResponseStart = useCallback(
    (update: SocketAiResponseStart) => {
      // Start frames always belong to a stream initiated by a bound
      // conversation, so an unbound chat can't legitimately receive one —
      // require an exact match rather than the lenient isForeignFrame rule.
      if (conversationId == null || update.message.conversation_id !== conversationId) {
        return;
      }
      if (cancelledMessageIdsRef.current.has(update.message.id)) {
        return;
      }
      setMessages((prev) => {
        if (prev.some((candidate) => candidate.serverMessageId === update.message.id)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: generateId(),
            clientKey: generateId(),
            content: '',
            conversation_id: update.message.conversation_id,
            isUser: false,
            serverMessageId: update.message.id,
            timestamp: new Date(),
            type: ChatSocketMessageType.aiResponseChunk,
          },
        ];
      });
    },
    [conversationId, setMessages]
  );

  const onFullMessageResponse = useCallback(
    (update: SocketMessageResponseComplete) => {
      if (isForeignFrame(update.message.conversation_id)) {
        return;
      }
      const { message } = update;

      // A complete frame ends the turn only when it finalizes the bubble the
      // stream rendered into. The flags are assigned idempotently, so a
      // re-invoked updater stays safe.
      let finalizedStreamBubble = false;
      let finalizedSourceTagged = false;
      setMessages((prev) => {
        // Streamed replies match on the placeholder id carried by the
        // start/chunk frames; `replaces_message_id` may differ from the final
        // `id` (the agent deletes the placeholder and persists fresh rows on
        // finalize), canceled-then-persisted turns keep it equal. Matching
        // either id in one pass relies on server row ids being unique across
        // the conversation — at most one bubble can ever match a frame.
        const targetIndex = prev.findIndex(
          (candidate) =>
            candidate.serverMessageId != null &&
            (candidate.serverMessageId === message.replaces_message_id ||
              candidate.serverMessageId === message.id)
        );
        if (targetIndex !== -1) {
          const target = prev[targetIndex];
          // A match on an already-complete bubble is a redelivered frame —
          // rewrite it idempotently but don't end the (possibly different)
          // in-flight turn.
          finalizedStreamBubble = target.type !== ChatSocketMessageType.aiResponseComplete;
          // Event-turn replies (FD-4182) stream like normal turns, but only
          // the complete frame carries the source marker — adopt it here so
          // the unprompted reply keeps its own bubble and heading. `?? undefined`
          // normalizes an explicit null from the unvalidated frame payload.
          const bubbleSource = target.source ?? message.additional_kwargs?.source ?? undefined;
          finalizedSourceTagged = bubbleSource != null;
          const next = [...prev];
          // Don't replace the message content - chunks have already built the full message.
          // The frame's content only fills a bubble that never received any
          // (zero-chunk streams, chunks lost to a reconnect).
          next[targetIndex] = {
            ...target,
            content: target.content || message.content,
            conversation_id: message.conversation_id,
            id: message.id,
            serverMessageId: message.id,
            source: bubbleSource,
            type: ChatSocketMessageType.aiResponseComplete,
          };
          return next;
        }
        if (message.replaces_message_id != null || message.additional_kwargs != null) {
          // Unmatched frame that still carries the FD-4131 identity contract.
          // Out-of-band deliveries only render into the conversation they
          // belong to — an unbound fresh chat must not adopt another
          // conversation's message.
          if (conversationId == null) {
            return prev;
          }
          // A frame with stream identity but no source is the turn's own reply
          // whose start/chunks never arrived (reconnect mid-generation): it
          // ends the turn. A source-tagged frame (an event turn's extra
          // persisted rows, a reconnect-delivered event reply, or a legacy
          // digest) leaves the in-flight stream's typing/turn state alone.
          finalizedStreamBubble = message.additional_kwargs?.source == null;
          return [
            ...prev,
            {
              id: message.id,
              clientKey: generateId(),
              content: message.content,
              conversation_id: message.conversation_id,
              feedback: message.feedback,
              isUser: false,
              serverMessageId: message.id,
              source: message.additional_kwargs?.source ?? undefined,
              timestamp: new Date(),
              type: ChatSocketMessageType.aiResponseComplete,
            },
          ];
        }
        // No identity on the frame (older agent deployment): the trailing
        // streaming bubble is the one that just finished.
        finalizedStreamBubble = true;
        const lastMessage = prev.at(-1);
        if (lastMessage && lastMessage.type === ChatSocketMessageType.aiResponseChunk) {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMessage,
              conversation_id: message.conversation_id,
              id: message.id,
              type: ChatSocketMessageType.aiResponseComplete,
            },
          ];
        }
        return prev;
      });

      if (!finalizedStreamBubble) {
        return;
      }
      // The finalized turn's own turn-scoped activity line clears either way.
      setAgentActivity(clearTurnActivity);
      // An event-turn reply's finalize must not end the user's turn state: the
      // user may have sent a message mid-event-turn, and their turn's frames
      // race the event turn's complete over the same socket. The three calls
      // below are no-ops for an event turn when no user turn is live.
      if (finalizedSourceTagged) {
        return;
      }
      setConversationId(message.conversation_id);
      setIsTyping(false);
      // We might get the full response before the cancellation request is resolved;
      // in some cases, the API will only send a full response message after a cancellation request
      // (rather than a generationCancelled message), so we must reset here.
      setIsCancelling(false);
    },
    [
      isForeignFrame,
      conversationId,
      setConversationId,
      setMessages,
      setIsTyping,
      setIsCancelling,
      setAgentActivity,
    ]
  );

  const onMessageResponseChunk = useCallback(
    (update: SocketMessageResponseChunk) => {
      if (isForeignFrame(update.conversation_id)) {
        return;
      }
      if (update.message_id != null && cancelledMessageIdsRef.current.has(update.message_id)) {
        return;
      }
      setMessages((prev) => {
        // Identified chunks route to the bubble created for their placeholder
        // id (which may no longer be the last message — e.g. a digest was
        // inserted mid-stream); unidentified ones (older agent deployment)
        // fall back to the trailing streaming bubble.
        const targetIndex =
          update.message_id != null
            ? prev.findIndex((candidate) => candidate.serverMessageId === update.message_id)
            : prev.at(-1)?.type === ChatSocketMessageType.aiResponseChunk
              ? prev.length - 1
              : -1;
        if (targetIndex === -1) {
          if (update.message_id != null && conversationId == null) {
            // An identified stream can only render into its own bound
            // conversation — same rule as the start frame.
            return prev;
          }
          return [
            ...prev,
            {
              id: generateId(),
              clientKey: generateId(),
              content: update.chunk,
              conversation_id: update.conversation_id ?? conversationId,
              isUser: false,
              serverMessageId: update.message_id,
              timestamp: new Date(),
              type: ChatSocketMessageType.aiResponseChunk,
            },
          ];
        }
        const target = prev[targetIndex];
        const next = [...prev];
        next[targetIndex] = { ...target, content: (target.content ?? '') + update.chunk };
        return next;
      });
    },
    [isForeignFrame, setMessages, conversationId]
  );

  const onTitleUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [ChatConversationsQueryKey] });
  }, []);

  /** End the in-flight turn locally after a cancel: typing off, the turn's
   *  activity line cleared, and the streaming bubble marked canceled. Shared by
   *  the WS `generation_canceled` frame and the REST cancel response — the
   *  REST endpoint stops the agent but never emits the frame (only the WS
   *  cancel handler does), so the UI has to reset itself. Idempotent: a
   *  bubble that is already finalized is left alone. */
  const finalizeCancelledTurn = useCallback(
    (
      cancelledConversationId: number | null | undefined,
      cancelledMessageId: number | null | undefined,
      partialContent: string | null | undefined
    ) => {
      if (cancelledMessageId != null) {
        cancelledMessageIdsRef.current.add(cancelledMessageId);
      }
      setIsCancelling(false);
      setIsTyping(false);
      setAgentActivity(clearTurnActivity);

      setMessages((prev) => {
        // Canceled turns update the placeholder row in place, so the cancelled
        // message id equals the id the stream rendered under; fall back to the
        // trailing unidentified streaming bubble (older agent deployment).
        let targetIndex =
          cancelledMessageId != null
            ? prev.findIndex(
                (candidate) =>
                  candidate.serverMessageId === cancelledMessageId &&
                  candidate.type === ChatSocketMessageType.aiResponseChunk
              )
            : -1;
        if (targetIndex === -1) {
          const lastMessage = prev.at(-1);
          if (
            lastMessage &&
            lastMessage.type === ChatSocketMessageType.aiResponseChunk &&
            lastMessage.serverMessageId == null
          ) {
            targetIndex = prev.length - 1;
          }
        }
        if (targetIndex === -1) {
          // else its a full message response or a user message, no need to update messages state
          return prev;
        }
        const target = prev[targetIndex];
        const next = [...prev];
        // The agent's persisted partial text is authoritative — it can run
        // ahead of what streamed (chunks still in flight are dropped above).
        // Cancelled before any text (tool phase) leaves nothing to show — drop
        // the bubble, as history does for empty rows (removeEmptyAgentMessages).
        const persisted = partialContent ?? '';
        const content = persisted.length >= target.content.length ? persisted : target.content;
        if (content === '') {
          next.splice(targetIndex, 1);
          return next;
        }
        next[targetIndex] = {
          ...target,
          content,
          conversation_id: cancelledConversationId ?? target.conversation_id,
          id: cancelledMessageId ?? target.id,
          type: ChatSocketMessageType.generationCanceled,
        };
        return next;
      });
    },
    [setIsCancelling, setIsTyping, setMessages, setAgentActivity]
  );

  const onGenerationCancelled = useCallback(
    (update: SocketGenerationCancelled) => {
      if (isForeignFrame(update.conversation_id)) {
        return;
      }
      finalizeCancelledTurn(update.conversation_id, update.message_id, update.partial_content);
    },
    [isForeignFrame, finalizeCancelledTurn]
  );

  const onAgentActivity = useCallback(
    (update: SocketAgentActivity) => {
      if (isForeignFrame(update.conversation_id)) {
        return;
      }
      setAgentActivity((prev) => reduceAgentActivity(prev, update));
    },
    [isForeignFrame, setAgentActivity]
  );

  // Socket error messages (type == 'error') don't necessarily mean the agent has stopped responding.
  // IsReceivingMessagesAtom is reset via useAgentErrorEffects when the overall agent health becomes 'error'.
  const onErrorMessage = useCallback(
    (update: SocketErrorResponse) => {
      // The agent sends a turn's error to every socket the user has open, so
      // one for another conversation belongs to another tab
      if (isForeignFrame(update.conversation_id)) {
        return;
      }
      setIsCancelling(false);
      setChatErrorState(update.message || 'An unexpected error occurred, please try again');
    },
    [isForeignFrame, setChatErrorState, setIsCancelling]
  );

  // The server reports the identity (user + effective tenant) on every
  // (re)connect; keep it in state so the UI can flag a socket bound to a
  // different tenant than the customer this host belongs to.
  const onConnectionEstablished = useCallback(
    (update: ISocketConnectedResponse) => {
      setAgentWsIdentity(update.user ? { userId: update.user.id, tenantId: update.user.tenant_id } : null);
    },
    [setAgentWsIdentity]
  );

  const { sendMessage: sendWsMessage } = useWsChat({
    onAiResponseStart,
    onConnectionEstablished,
    onError: onErrorMessage,
    onFullMessageResponse,
    onGenerationCancelled,
    onMessageResponseChunk,
    onTitleUpdated,
    onAgentActivity,
  });

  const uiContext = useAtomValue(ChatUiContextAtom);

  const sendMessage = useCallback(
    async (content: string) => {
      const conversationId = await getConversationId();
      if (!conversationId) {
        return;
      }
      const userMessage: MessageData = {
        id: generateId(),
        clientKey: generateId(),
        content,
        conversation_id: conversationId,
        isUser: true,
        timestamp: new Date(),
        type: ChatSocketMessageType.chat,
      };

      // Attach the current UI context (e.g. the selected recon run) so the agent
      // can resolve "this run" without an id. Rides alongside the
      // message; not part of the persisted MessageData shown locally.
      const payload = uiContext ? { ...userMessage, context: uiContext } : userMessage;
      sendWsMessage(payload as IChatMessage);

      setMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);
    },
    [getConversationId, sendWsMessage, setMessages, setIsTyping, uiContext]
  );

  const onCancelGeneration = useCallback(async () => {
    if (!conversationId) {
      // should not happen
      console.warn('No conversation id to cancel generation');
      Sentry.logger.error('useChat: No conversation id to cancel generation');
      return;
    }
    setIsCancelling(true);
    try {
      const response = await cancelAgentQuery(conversationId);
      // `cancelled: false` means nothing was running yet (a tap in the window
      // before the agent registered the turn) — leave the turn's state alone
      // so Stop stays available and its own terminal frame ends it.
      if (response.cancelled) {
        finalizeCancelledTurn(conversationId, response.message_id, response.partial_content);
      }
    } catch (err) {
      console.error(err);
      Sentry.logger.error('useChat: Error cancelling generation', { error: err });
      setChatErrorState(getErrorMessage(err, 'Something went wrong, please try again'));
    } finally {
      setIsCancelling(false);
    }
  }, [conversationId, setIsCancelling, setChatErrorState, finalizeCancelledTurn]);

  return {
    isCancelling,
    isTyping,
    messages,
    onCancelGeneration,
    sendMessage,
  };
};
